import React, { memo, useRef } from 'react';
import { SearchNavigationState } from '../../hooks/useSearchNavigation';
import { renderWordsAsHTML } from '../../utils/wordRendering';
import { TranscriptUtterance } from '../../types';

interface EditorUtteranceContentProps {
  utterance: TranscriptUtterance;
  needsDOMSync: boolean;
  isCurrentUtterance: boolean;
  currentWordIndex: number;
  isPlaying: boolean;
  searchState: SearchNavigationState;
  onUtteranceClick: (e: React.MouseEvent, utteranceId: string) => void;
  onKeyDown: (e: React.KeyboardEvent, utteranceId: string) => void;
  onInput: (e: React.FormEvent<HTMLDivElement>, utteranceId: string) => void;
  onPaste: (e: React.ClipboardEvent<HTMLDivElement>) => void;
}

const EditorUtteranceContent: React.FC<EditorUtteranceContentProps> = ({
  utterance,
  needsDOMSync,
  isCurrentUtterance,
  currentWordIndex,
  isPlaying,
  searchState,
  onUtteranceClick,
  onKeyDown,
  onInput,
  onPaste,
}) => {
  const isInitializedRef = useRef(false);

  return (
    <div
      data-tutorial="utterance-content"
      contentEditable
      suppressContentEditableWarning
      className="min-h-[4rem] p-4 focus:outline-none text-gray-900 dark:text-white leading-relaxed utterance-content"
      onKeyDown={(e) => onKeyDown(e, utterance.id)}
      onInput={(e) => {
        if (!needsDOMSync) {
          onInput(e, utterance.id);
        }
      }}
      onClick={(e) => onUtteranceClick(e, utterance.id)}
      onPaste={onPaste}
      style={{
        wordWrap: 'break-word',
        overflowWrap: 'break-word',
      }}
      ref={(el) => {
        if (el) {
          const newHTML = renderWordsAsHTML(
            utterance.id,
            utterance.transcriptWords,
            isCurrentUtterance ? currentWordIndex : -1,
            isCurrentUtterance && isPlaying,
            searchState
          );
          const hasActiveFocus = document.activeElement === el;

          // Only update content if:
          // 1. Initial render (!isInitializedRef.current)
          // 2. Structural changes (needsDOMSync)
          // 3. Content is different (always update to maintain proper styling)
          if (
            !isInitializedRef.current ||
            needsDOMSync ||
            el.innerHTML !== newHTML
          ) {
            // Save cursor position as character offset if element has focus
            let savedOffset = -1;
            if (hasActiveFocus) {
              const selection = window.getSelection();
              if (selection && selection.rangeCount > 0) {
                const range = selection.getRangeAt(0);
                // Calculate character offset from the start of the element
                const preCaretRange = range.cloneRange();
                preCaretRange.selectNodeContents(el);
                preCaretRange.setEnd(range.endContainer, range.endOffset);
                savedOffset = preCaretRange.toString().length;
              }
            }

            // Update content
            el.innerHTML = newHTML;
            isInitializedRef.current = true;

            // Restore cursor position if we had focus
            if (hasActiveFocus && savedOffset >= 0) {
              try {
                // Get the new text content to validate offset
                const newTextContent = el.textContent || '';
                const safeOffset = Math.min(savedOffset, newTextContent.length);

                // Find the text node and offset for the saved position
                const walker = document.createTreeWalker(
                  el,
                  NodeFilter.SHOW_TEXT,
                  null
                );

                let currentPos = 0;
                let node = walker.nextNode();
                let targetNode = null;
                let targetOffset = 0;

                while (node) {
                  const nodeLength = node.textContent?.length || 0;
                  if (currentPos + nodeLength >= safeOffset) {
                    // Found the node containing our cursor position
                    targetNode = node;
                    targetOffset = Math.min(
                      safeOffset - currentPos,
                      nodeLength
                    );
                    break;
                  }
                  currentPos += nodeLength;
                  node = walker.nextNode();
                }

                // If we found a target node, set the cursor there
                if (targetNode) {
                  const selection = window.getSelection();
                  if (selection) {
                    const range = document.createRange();
                    range.setStart(targetNode, targetOffset);
                    range.collapse(true);
                    selection.removeAllRanges();
                    selection.addRange(range);
                  }
                } else if (node === null && walker.currentNode) {
                  // No node found at the exact position, try to place cursor at the end
                  // of the last text node if we have content
                  const lastWalker = document.createTreeWalker(
                    el,
                    NodeFilter.SHOW_TEXT,
                    null
                  );
                  let lastNode = null;
                  let currentNode = lastWalker.nextNode();
                  while (currentNode) {
                    lastNode = currentNode;
                    currentNode = lastWalker.nextNode();
                  }

                  if (lastNode) {
                    const selection = window.getSelection();
                    if (selection) {
                      const range = document.createRange();
                      const nodeLength = lastNode.textContent?.length || 0;
                      range.setStart(
                        lastNode,
                        Math.min(safeOffset - currentPos, nodeLength)
                      );
                      range.collapse(true);
                      selection.removeAllRanges();
                      selection.addRange(range);
                    }
                  }
                }
              } catch (e) {
                // Cursor restoration failed, ignore
                console.warn('Cursor restoration failed:', e);
              }
            }
          }
        }
      }}
    />
  );
};

export default memo(EditorUtteranceContent);
